import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Home, Calendar, Users, Heart, ImageIcon, Phone, HelpCircle, FileText, Award,
  Newspaper, Menu, ChevronDown, Sparkles, Eye, Shield
} from 'lucide-react';
import { Button } from '../ui/button';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger
} from '../ui/dropdown-menu';
import { Sheet, SheetContent, SheetTrigger, SheetClose } from '../ui/sheet';
import { MandalaCorner } from './MandalaCorner';
import { motion } from 'framer-motion';
import { api } from '../../App';
import { broadcastHearts, onHeartsChange } from '../../lib/heartSync';
import { usePageContent } from '../../lib/content';
import { WhatsAppButton } from './WhatsAppButton';
import { FestivalDecor } from './FestivalDecor';

// Primary navigation (always visible on desktop)
const PRIMARY_NAV = [
  { path: '/', label: 'Home', icon: Home },
  { path: '/events', label: 'Events', icon: Calendar },
  { path: '/newsletters', label: 'Newsletters', icon: Newspaper },
  { path: '/membership', label: 'Membership', icon: Users },
  { path: '/donations', label: 'Donate', icon: Heart },
];

// Grouped under Community Hub dropdown
const HUB_NAV = [
  { path: '/about', label: 'About Us', icon: FileText },
  { path: '/saturday-club', label: 'Saturday Club', icon: Calendar },
  { path: '/gallery', label: 'Gallery', icon: ImageIcon },
  { path: '/sponsors', label: 'Sponsors', icon: Award },
  { path: '/volunteer', label: 'Volunteer', icon: Heart },
  { path: '/faq', label: 'FAQ', icon: HelpCircle },
  { path: '/contact', label: 'Contact', icon: Phone },
  { path: '/privacy-policy', label: 'Privacy Policy', icon: Shield },
];

const THEME_RIBBONS = {
  diwali: 'Shubh Deepavali — Wishing our community a Happy Diwali',
  holi: 'Happy Holi — Holika Dahan & the Festival of Colours',
  navratri: 'Jay Mataji — Navratri Garba Celebrations',
  uttarayan: 'Happy Uttarayan — Kai Po Che! Makar Sankranti (14th January)',
  gujarat_day: 'Jay Jay Garvi Gujarat — Gujarat Sthapna Din (1st May)',
  independence_day: 'Happy Independence Day — Jai Hind (15th August)',
  republic_day: 'Happy Republic Day — Jai Hind (26th January)',
};

export const LuxuryLayout = ({ children }) => {
  const { t: ft } = usePageContent('footer');
  const { pathname } = useLocation();
  // Event details / ticket booking, donation and membership pages — decorative side illustrations
  // are hidden on these only, per request, so the forms have uninterrupted space either side.
  const isEventDetailPage = /^\/(events\/[^/]+|donations|membership)(\/|$)/.test(pathname);
  const [scrolled, setScrolled] = React.useState(false);
  const [theme, setTheme] = React.useState('default');
  const [hearts, setHearts] = React.useState(0);
  const [visits, setVisits] = React.useState(0);
  const [heartPop, setHeartPop] = React.useState(false);
  const [showCookieNotice, setShowCookieNotice] = React.useState(false);

  React.useEffect(() => {
    try {
      if (!localStorage.getItem('gs_cookie_consent')) setShowCookieNotice(true);
    } catch { /* private browsing */ }
  }, []);

  const acceptCookies = () => {
    try { localStorage.setItem('gs_cookie_consent', new Date().toISOString()); } catch {}
    setShowCookieNotice(false);
  };

  React.useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  React.useEffect(() => {
    api.get('/api/public/settings').then(s => setTheme(s.site_theme || 'default')).catch(() => {});
    api.get('/api/metrics').then(m => { setHearts(m.hearts || 0); setVisits(m.visits || 0); }).catch(() => {});
    // Keep navbar heart in sync when the home-page heart is tapped
    return onHeartsChange((count) => setHearts(count));
  }, []);

  const handleHeartTap = async () => {
    setHeartPop(true);
    setTimeout(() => setHeartPop(false), 400);
    setHearts(h => { const next = h + 1; broadcastHearts(next); return next; });
    try {
      const res = await api.post('/api/metrics/heart', {});
      if (typeof res?.hearts === 'number') { setHearts(res.hearts); broadcastHearts(res.hearts); }
    } catch {}
  };

  return (
    <div className={`luxury-root min-h-screen bg-[hsl(var(--lux-ivory))] theme-${theme}`} data-theme="luxury" data-testid="luxury-root">
      {/* Festive theme decorations (torans, dancers, bonfire, fireworks…) — side dancer illustrations
          are suppressed on the event details / ticket booking page only; everything else is unchanged. */}
      <FestivalDecor theme={theme} hideDancers={isEventDetailPage} />
      {/* Festive theme ribbon */}
      {THEME_RIBBONS[theme] && (
        <div className="festive-ribbon flex items-center justify-center gap-2" data-testid="festive-ribbon">
          <Sparkles className="w-3.5 h-3.5" /> {THEME_RIBBONS[theme]} <Sparkles className="w-3.5 h-3.5" />
        </div>
      )}

      {/* Luxury Navbar */}
      <motion.nav
        className={`sticky top-0 z-40 transition-all duration-300 ${
          scrolled ? 'bg-white/90 backdrop-blur-md shadow-lg' : 'bg-transparent'
        }`}
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-3 group" data-testid="luxury-nav-logo">
              <img
                src="https://customer-assets-0z36b82j.emergentagent.net/job_notts-gujarati/artifacts/8ofk94lm_GS_logo.webp"
                alt="Gujarat Samaj Logo"
                className="w-14 h-14 lg:w-16 lg:h-16 object-contain flex-shrink-0"
              />
              <div className="text-sm font-bold text-[hsl(var(--lux-royal))] leading-tight whitespace-nowrap" style={{ fontFamily: 'Cormorant Garamond' }}>
                Gujarat Samaj<br />Nottingham
              </div>
            </Link>

            {/* Desktop Nav */}
            <div className="hidden lg:flex items-center gap-0.5">
              {PRIMARY_NAV.map((item) => (
                <Link key={item.path} to={item.path}>
                  <Button
                    variant="ghost"
                    className="text-sm font-medium text-[hsl(var(--lux-ink))] hover:text-[hsl(var(--lux-royal))] hover:bg-[hsl(var(--lux-royal-soft))] transition-colors px-3"
                    data-testid={`luxury-nav-${item.label.toLowerCase()}`}
                  >
                    <item.icon className="w-4 h-4 mr-1.5" />
                    {item.label}
                  </Button>
                </Link>
              ))}
              {/* Community Hub dropdown */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    className="text-sm font-medium text-[hsl(var(--lux-ink))] hover:text-[hsl(var(--lux-royal))] hover:bg-[hsl(var(--lux-royal-soft))] transition-colors px-3"
                    data-testid="luxury-nav-community-hub"
                  >
                    <Sparkles className="w-4 h-4 mr-1.5" />
                    Community Hub
                    <ChevronDown className="w-3.5 h-3.5 ml-1" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  {HUB_NAV.map((item) => (
                    <DropdownMenuItem key={item.path} asChild>
                      <Link to={item.path} className="flex items-center gap-2 cursor-pointer" data-testid={`hub-nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}>
                        <item.icon className="w-4 h-4 text-[hsl(var(--lux-gold))]" />
                        {item.label}
                      </Link>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            {/* Right side: heart counter + CTAs + hamburger */}
            <div className="flex items-center gap-2">
              {/* Tappable heart counter (top) — circular like a badge */}
              <button
                onClick={handleHeartTap}
                aria-label="Tap the heart to show your love"
                className="relative w-11 h-11 rounded-full border-2 border-[hsl(var(--lux-vermilion)/0.35)] bg-[hsl(var(--lux-vermilion)/0.1)] hover:bg-[hsl(var(--lux-vermilion)/0.18)] hover:border-[hsl(var(--lux-vermilion))] active:scale-90 transition-all cursor-pointer flex items-center justify-center flex-shrink-0"
                data-testid="navbar-heart-button"
                title="Tap to show your love!"
              >
                <motion.div animate={heartPop ? { scale: [1, 1.5, 1] } : {}} transition={{ duration: 0.4 }}>
                  <Heart className="w-5 h-5 text-[hsl(var(--lux-vermilion))]" fill="currentColor" />
                </motion.div>
                <span className="absolute -top-1.5 -right-1.5 min-w-[20px] h-5 px-1 rounded-full bg-[hsl(var(--lux-vermilion))] text-white text-[10px] font-bold flex items-center justify-center tabular-nums" data-testid="navbar-heart-count">
                  {hearts > 999 ? '999+' : hearts.toLocaleString()}
                </span>
              </button>

              <Link to="/membership" className="hidden sm:block">
                <Button
                  className="bg-[hsl(var(--lux-royal))] text-white hover:bg-[hsl(var(--lux-royal-2))] shadow-md hover:shadow-lg transition-all"
                  data-testid="luxury-nav-join-button"
                >
                  <Users className="w-4 h-4 mr-2" />
                  Join
                </Button>
              </Link>

              {/* Mobile hamburger (3 lines) */}
              <Sheet>
                <SheetTrigger asChild>
                  <Button variant="ghost" size="icon" className="lg:hidden text-[hsl(var(--lux-royal))]" data-testid="mobile-menu-button" aria-label="Open menu">
                    <Menu className="w-7 h-7" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="right" className="w-72 bg-[hsl(var(--lux-ivory))] overflow-y-auto" data-testid="mobile-menu-drawer">
                  <div className="flex items-center gap-2 mb-6 mt-2">
                    <img
                      src="https://customer-assets-0z36b82j.emergentagent.net/job_notts-gujarati/artifacts/8ofk94lm_GS_logo.webp"
                      alt="GS Logo" className="w-10 h-10 object-contain"
                    />
                    <span className="font-bold text-[hsl(var(--lux-royal))]" style={{ fontFamily: 'Cormorant Garamond' }}>Gujarat Samaj</span>
                  </div>
                  <nav className="space-y-1">
                    {PRIMARY_NAV.map((item) => (
                      <SheetClose asChild key={item.path}>
                        <Link
                          to={item.path}
                          className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-[hsl(var(--lux-ink))] hover:bg-[hsl(var(--lux-royal-soft))] hover:text-[hsl(var(--lux-royal))] font-medium transition-colors"
                          data-testid={`mobile-nav-${item.label.toLowerCase()}`}
                        >
                          <item.icon className="w-5 h-5 text-[hsl(var(--lux-gold))]" />
                          {item.label}
                        </Link>
                      </SheetClose>
                    ))}
                    <div className="pt-3 pb-1 px-3 text-xs font-bold uppercase tracking-wider text-[hsl(var(--lux-ink)/0.5)]">Community Hub</div>
                    {HUB_NAV.map((item) => (
                      <SheetClose asChild key={item.path}>
                        <Link
                          to={item.path}
                          className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-[hsl(var(--lux-ink))] hover:bg-[hsl(var(--lux-royal-soft))] hover:text-[hsl(var(--lux-royal))] font-medium transition-colors"
                          data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
                        >
                          <item.icon className="w-5 h-5 text-[hsl(var(--lux-gold))]" />
                          {item.label}
                        </Link>
                      </SheetClose>
                    ))}
                    <SheetClose asChild>
                      <Link to="/membership" className="block mt-4">
                        <Button className="w-full bg-[hsl(var(--lux-royal))] text-white" data-testid="mobile-nav-join-button">
                          <Users className="w-4 h-4 mr-2" /> Join Membership
                        </Button>
                      </Link>
                    </SheetClose>
                  </nav>
                </SheetContent>
              </Sheet>
            </div>
          </div>
        </div>
        {/* Gold bottom border */}
        <div className="h-px bg-gradient-to-r from-transparent via-[hsl(var(--lux-gold)/0.6)] to-transparent" />
      </motion.nav>

      {/* Main Content */}
      <main className="relative">{children}</main>

      {/* Cookie consent notice (essential cookies only) */}
      {showCookieNotice && (
        <div className="fixed bottom-20 left-4 right-4 sm:bottom-6 sm:left-1/2 sm:right-auto sm:-translate-x-1/2 sm:w-full sm:max-w-md z-[300]" role="dialog" aria-label="Cookie notice" data-testid="cookie-consent-banner">
          <div className="rounded-xl border-2 border-[hsl(var(--lux-gold)/0.6)] bg-white/95 backdrop-blur-md shadow-xl p-4">
            <p className="text-sm text-[hsl(var(--lux-ink))] leading-relaxed">
              We use only <strong>essential cookies</strong> to make this site work — no advertising or tracking cookies.
              See our{' '}
              <Link to="/privacy-policy" className="text-[hsl(var(--lux-royal))] underline font-medium" data-testid="cookie-privacy-link">
                Privacy Policy
              </Link>{' '}for details.
            </p>
            <div className="mt-3 flex justify-end">
              <Button size="sm" onClick={acceptCookies} className="bg-[hsl(var(--lux-royal))] text-white hover:bg-[hsl(var(--lux-royal-2))]" data-testid="cookie-accept-button">
                Got it
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Luxury Footer */}
      <footer className="relative bg-[hsl(var(--lux-midnight))] text-white overflow-hidden">
        <MandalaCorner position="top-left" className="opacity-20" animate={false} />
        <MandalaCorner position="top-right" className="opacity-20" animate={false} />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-10 mb-12">
            {/* Brand */}
            <div>
              <h3 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Cormorant Garamond' }}>
                {ft('brand_title', 'Gujarat Samaj Nottingham')}
              </h3>
              <p className="text-sm text-white/70 leading-relaxed mb-4">
                {ft('brand_text', 'Celebrating Gujarati culture and heritage in Nottingham since our founding.')}
              </p>
              <div className="inline-block bg-white/10 rounded-lg px-4 py-2 backdrop-blur-sm border border-[hsl(var(--lux-gold)/0.3)]">
                <p className="text-sm font-semibold text-white">
                  {ft('charity_line', 'Charity No. 513936')}
                </p>
              </div>
            </div>

            {/* Quick Links (primary tabs) */}
            <div data-testid="footer-quick-links">
              <h4 className="text-lg font-semibold mb-4 text-[hsl(var(--lux-gold))]" style={{ fontFamily: 'Cormorant Garamond' }}>
                Quick Links
              </h4>
              <ul className="space-y-2 text-sm">
                {PRIMARY_NAV.map((item) => (
                  <li key={item.path}>
                    <Link to={item.path} className="text-white/70 hover:text-white transition-colors">
                      {item.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Community Hub (grouped tabs) */}
            <div data-testid="footer-community-hub">
              <h4 className="text-lg font-semibold mb-4 text-[hsl(var(--lux-gold))]" style={{ fontFamily: 'Cormorant Garamond' }}>
                Community Hub
              </h4>
              <ul className="space-y-2 text-sm">
                {HUB_NAV.map((item) => (
                  <li key={item.path}>
                    <Link to={item.path} className="text-white/70 hover:text-white transition-colors">
                      {item.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Contact */}
            <div>
              <h4 className="text-lg font-semibold mb-4 text-[hsl(var(--lux-gold))]" style={{ fontFamily: 'Cormorant Garamond' }}>
                {ft('contact_heading', 'Get in Touch')}
              </h4>
              <ul className="space-y-2 text-sm text-white/70">
                <li>Email: <a href={`mailto:${ft('contact_email', 'info@gujaratsamajnottingham.com')}`} className="hover:text-[hsl(var(--lux-gold))]" data-testid="footer-email-link">{ft('contact_email', 'info@gujaratsamajnottingham.com')}</a></li>
                {ft('whatsapp', '') && <li>WhatsApp: <a href={`https://wa.me/${String(ft('whatsapp', '')).replace(/[^0-9]/g, '')}`} target="_blank" rel="noreferrer" className="hover:text-[hsl(var(--lux-gold))]" data-testid="footer-whatsapp-link">{ft('whatsapp', '')}</a></li>}
                <li>{ft('contact_location', 'Nottingham, United Kingdom')}</li>
              </ul>
              <div className="flex items-center gap-3 mt-4" data-testid="footer-social-links">
                <a href={ft('facebook_url', 'https://www.facebook.com/share/1UaeYDTuzR/?mibextid=wwXIfr')} target="_blank" rel="noreferrer" aria-label="Facebook" data-testid="footer-facebook-link"
                  className="w-9 h-9 rounded-full bg-white/10 border border-[hsl(var(--lux-gold)/0.25)] flex items-center justify-center hover:bg-[hsl(var(--lux-gold)/0.2)] transition-colors">
                  <svg className="w-4 h-4 text-[hsl(var(--lux-gold))]" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                </a>
                <a href={ft('instagram_url', 'https://www.instagram.com/gujaratsamajnotts?igsi=MWtrNzFwc2I5NWRjYg%3D%3D&utm_source=qr')} target="_blank" rel="noreferrer" aria-label="Instagram" data-testid="footer-instagram-link"
                  className="w-9 h-9 rounded-full bg-white/10 border border-[hsl(var(--lux-gold)/0.25)] flex items-center justify-center hover:bg-[hsl(var(--lux-gold)/0.2)] transition-colors">
                  <svg className="w-4 h-4 text-[hsl(var(--lux-gold))]" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>
                </a>
                {ft('whatsapp', '') && (
                  <a href={`https://wa.me/${String(ft('whatsapp', '')).replace(/[^0-9]/g, '')}`} target="_blank" rel="noreferrer" aria-label="WhatsApp" title="Chat with us on WhatsApp" data-testid="footer-whatsapp-icon-link"
                    className="w-9 h-9 rounded-full bg-[hsl(var(--brand-whatsapp))] border border-white/30 flex items-center justify-center hover:bg-[hsl(var(--brand-whatsapp-dark))] transition-colors">
                    <svg className="w-4 h-4 text-white" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                  </a>
                )}
                {ft('youtube_url', '') && (
                  <a href={ft('youtube_url', '')} target="_blank" rel="noreferrer" aria-label="YouTube" data-testid="footer-youtube-link"
                    className="w-9 h-9 rounded-full bg-white/10 border border-[hsl(var(--lux-gold)/0.25)] flex items-center justify-center hover:bg-[hsl(var(--lux-gold)/0.2)] transition-colors">
                    <svg className="w-4 h-4 text-[hsl(var(--lux-gold))]" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>
                  </a>
                )}
              </div>
            </div>
          </div>

          <div className="h-px bg-gradient-to-r from-transparent via-[hsl(var(--lux-gold)/0.3)] to-transparent mb-8" />

          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mb-6" data-testid="footer-touches">
            <div className="flex items-center gap-1.5 bg-white/5 border border-[hsl(var(--lux-gold)/0.25)] rounded-full px-2.5 py-0.5">
              <Eye className="w-3 h-3 text-[hsl(var(--lux-gold))]" />
              <span className="text-sm font-bold text-[hsl(var(--lux-gold))] tabular-nums" style={{ fontFamily: 'Cormorant Garamond' }} data-testid="footer-touches-value">
                {visits.toLocaleString()}
              </span>
              <span className="text-[11px] text-white/60 font-medium tracking-wide">Touches</span>
            </div>
          </div>

          <div className="text-center text-sm text-white/50">
            <p>© {new Date().getFullYear()} {ft('copyright_text', 'Gujarat Samaj Nottingham. All rights reserved.')}</p>
          </div>
        </div>
      </footer>
      <WhatsAppButton />
    </div>
  );
};
